const textToNumber = (text, options) => {
    const numberRegex = /^-?[\d,.]+$/;
    if ((numberRegex.exec(text)) === null) {
        return text;
    }
    const parsed = Number.parseFloat(text);
    return Number.isNaN(parsed) ? text : { value: parsed, options };
};
export default {
    amount: ({ data, override, overrideMap }) => {
        if (overrideMap?.amount && typeof overrideMap.amount === 'string') {
            return textToNumber(overrideMap.amount);
        }
        if (override?.nominal?.amount?.size !== undefined) {
            return override.nominal.amount.size.print();
        }
        if (data?.nominal?.tapAmount?.size !== undefined) {
            return data.nominal.tapAmount.size.print();
        }
        if (data?.nominal?.amount?.size !== undefined) {
            return data.nominal.amount.size.print();
        }
        return undefined;
    },
    benchmark: ({ data, override, overrideMap }) => {
        if (overrideMap?.benchmark !== undefined) {
            return overrideMap.benchmark;
        }
        const genericBenchmark = override?.pricing?.spread?.benchmark ?? data?.pricing?.spread?.benchmark;
        return genericBenchmark?.print();
    },
    book: ({ data, override, overrideMap }) => {
        if (overrideMap?.books && typeof overrideMap.books === 'string') {
            return textToNumber(overrideMap.books);
        }
        const items = [];
        const bookValue = override?.management?.books !== undefined ? override?.management?.books?.size?.value : data?.management?.books?.size?.value;
        if (bookValue !== undefined) {
            items.push({ value: bookValue / 1000000 });
        }
        const combinedBookValue = override?.management?.combinedBooks !== undefined ? override?.management?.combinedBooks?.size?.value : data?.management?.combinedBooks?.size?.value;
        if (combinedBookValue !== undefined) {
            items.push({ value: combinedBookValue / 1000000, suffix: "(Comb.)" });
        }
        return items.length > 0 ? items : undefined;
    },
    bookrunners: ({ meta, overrideMap }) => {
        if (overrideMap?.bookrunners && typeof overrideMap.bookrunners === 'string') {
            return overrideMap.bookrunners;
        }
        if (meta?.bookrunners !== undefined) {
            const realBookrunners = meta?.bookrunners.filter(b => b.isRealBookrunner() && !b.override?.ignore);
            if (realBookrunners.length > 8) {
                const jgcs = realBookrunners.filter((b) => b.roles?.includes('GLOBAL_COORDINATOR'));
                if (jgcs.length === 1) {
                    return `SGC: ${jgcs[0].printName()}`;
                }
                else if (jgcs.length < 9) {
                    return `JGCs: ${jgcs.map((m) => m.printName()).sort((a, b) => a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase())).join(', ')}`;
                }
                const physicalBookrunners = realBookrunners.filter(b => b.roles?.includes('BOOK_RUNNER') && b.roleType === 'PHYSICAL');
                if (physicalBookrunners.length > 0) {
                    return `Physical BRs: ${physicalBookrunners.map((m) => m.printName()).sort((a, b) => a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase())).join(', ')}`;
                }
            }
            return realBookrunners.map((m) => m.printName()).sort((a, b) => a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase())).join(', ');
        }
        return undefined;
    },
    collateral: () => {
        return "TBC";
    },
    company: ({ transaction }) => {
        if (transaction.company === undefined) {
            return undefined;
        }
        const { company, name, type } = transaction.company;
        if (company?.name !== undefined) {
            let typeText;
            switch (type) {
                case 'GUARANTOR':
                    typeText = ' (Guar.)';
                    break;
                case 'OBLIGOR':
                    typeText = ' (Obl.)';
                    break;
                default:
                    typeText = '';
            }
            return company.name + typeText;
        }
        return name;
    },
    coupon: ({ data, override, overrideMap }) => {
        if (overrideMap?.coupon && typeof overrideMap.coupon === 'string') {
            return textToNumber(overrideMap.coupon, { minimumFractionDigits: 2 });
        }
        if (override?.coupons?.[0].fixedRate !== undefined || override?.coupons?.[0].floatingRate !== undefined) {
            const [coupon] = override.coupons;
            return coupon.print();
        }
        if (data !== undefined) {
            const coupons = data.coupons ?? data.profitRates;
            if (coupons !== undefined) {
                const [coupon] = coupons;
                return coupon.print();
            }
        }
        return undefined;
    },
    currency: ({ data, override, overrideMap }) => {
        return override?.nominal?.amount?.currency ?? overrideMap?.currency ?? data?.nominal?.amount?.currency ?? data?.nominal?.tapAmount?.currency;
    },
    cusip: ({ data, overrideMap }) => {
        if (overrideMap?.cusip !== undefined) {
            return overrideMap.cusip;
        }
        if (data?.identifiers !== undefined) {
            const isins = data.identifiers.filter((i) => i.type === 'CUSIP' && 'value' in i);
            if (isins.length === 1) {
                return isins[0].value;
            }
            if (isins.length > 1) {
                const isin144A = isins.find(i => i.regulation === 'RULE_144A');
                if (isin144A !== undefined) {
                    return isin144A.value;
                }
                return isins[0].value;
            }
        }
        return undefined;
    },
    identifier: (identifierType, regulationsType) => ({ data, override, overrideMap }) => {
        const regulation = (t) => {
            if (t === 'REGS')
                return 'RegS';
            if (t === 'RULE_144A')
                return '144A';
            return '';
        };
        const field = identifierType.toLowerCase() + regulation(regulationsType);
        if (override?.identifiers !== undefined) {
            const identifies = override.identifiers.find((i) => i.type === identifierType && 'value' in i && regulationsType === i.regulation);
            return identifies?.value;
        }
        if (overrideMap && field in overrideMap) {
            return overrideMap[field];
        }
        if (data?.identifiers !== undefined) {
            const identifies = data.identifiers.find((i) => i.type === identifierType && 'value' in i && regulationsType === i.regulation);
            return identifies?.value;
        }
        return undefined;
    },
    documentation: (field) => ({ data, override, overrideMap }) => {
        if (override?.documentation?.[field] !== undefined) {
            return override.documentation[field];
        }
        if (overrideMap && field in overrideMap) {
            return overrideMap[field];
        }
        if (data?.documentation?.[field] !== undefined) {
            return data.documentation[field];
        }
        return undefined;
    },
    documentationArray: (field) => ({ data, override, overrideMap }) => {
        if (override?.documentation?.[field] !== undefined) {
            return override.documentation[field];
        }
        if (overrideMap && field in overrideMap && typeof overrideMap[field] === 'string') {
            const val = overrideMap[field];
            return val.split(',');
        }
        if (data?.documentation?.[field] !== undefined) {
            return data.documentation[field];
        }
        return undefined;
    },
    expectedMaturityDate: () => {
        return "TBC";
    },
    firstPayDate: ({ data }) => {
        if (data?.coupons !== undefined && data.coupons.length > 0) {
            return data.coupons[0].firstCouponDate;
        }
        return undefined;
    },
    guaranteed: ({ data, meta, override }) => {
        if (override?.guarantee !== undefined) {
            return true;
        }
        if (data?.guarantee !== undefined) {
            return true;
        }
        if (meta?.parties?.some(p => p.type === 'GUARANTOR')) {
            return true;
        }
        return undefined;
    },
    guidance: ({ data, override, overrideMap }) => {
        if (overrideMap?.guidance) {
            return overrideMap.guidance;
        }
        const guidance = override?.guidance ?? data?.guidance;
        return guidance?.print();
    },
    initialPriceThoughts: ({ data, override, overrideMap }) => {
        if (overrideMap?.initialPriceThoughts) {
            return overrideMap.initialPriceThoughts;
        }
        const initialPriceThoughts = override?.initialPriceThoughts ?? data?.initialPriceThoughts;
        return initialPriceThoughts?.print();
    },
    industry: ({ meta, overrideMap }) => {
        if (overrideMap?.industry) {
            return overrideMap.industry;
        }
        const issuer = meta?.parties?.find(p => p.type === 'ISSUER');
        if (issuer?.company?.sector !== undefined) {
            return issuer.company.sector;
        }
        return undefined;
    },
    interestBasis: ({ data, override, overrideMap }) => {
        if (overrideMap?.interestBasis) {
            return overrideMap.interestBasis;
        }
        else if (overrideMap?.coupon) {
            return Number.isNaN(Number.parseFloat(overrideMap.coupon)) ? 'FLOATING' : 'FIXED';
        }
        else if (override?.coupons !== undefined) {
            const [coupon] = override.coupons;
            return coupon.printInterestBasis();
        }
        else if (data?.coupons !== undefined) {
            const [coupon] = data.coupons;
            return coupon.printInterestBasis();
        }
        else if (data?.profitRates !== undefined) {
            const [coupon] = data.profitRates;
            return coupon.printInterestBasis();
        }
        return undefined;
        // return ib !== undefined ? map.get(ib) : undefined;
    },
    iptMove: (tranche) => {
        const basis = tranche.basis();
        const { data, override, overrideMap } = tranche;
        if (overrideMap?.iptMove) {
            return textToNumber(overrideMap.iptMove);
        }
        const pricing = override?.pricing ?? data?.pricing;
        const initialPriceThoughts = override?.initialPriceThoughts ?? data?.initialPriceThoughts;
        const guidance = override?.guidance ?? data?.guidance;
        const revisedGuidance = override?.revisedGuidance ?? data?.revisedGuidance;
        if (pricing !== undefined) {
            if (pricing.spread?.value !== undefined && basis === 'spread') {
                const { value } = pricing.spread;
                const initial = initialPriceThoughts?.spread?.value?.value ?? initialPriceThoughts?.spread?.range?.min?.value ?? guidance?.spread?.value?.value ?? guidance?.spread?.range?.min?.value ?? revisedGuidance?.spread?.value?.value ?? revisedGuidance?.spread?.range?.min?.value;
                if (initial !== undefined) {
                    const reoffer = value;
                    return { value: reoffer - initial };
                }
            }
            if (pricing.yield?.value !== undefined && basis === 'yield') {
                const initial = initialPriceThoughts?.yield?.value?.value ?? initialPriceThoughts?.yield?.range?.min?.value ?? guidance?.yield?.value?.value ?? guidance?.yield?.range?.min?.value ?? revisedGuidance?.yield?.value?.value ?? revisedGuidance?.yield?.range?.min?.value;
                if (initial !== undefined) {
                    const reofferYield = pricing.yield.value;
                    return { value: 100 * (reofferYield - initial) };
                }
            }
        }
        return undefined;
    },
    isin: ({ data, overrideMap }) => {
        if (overrideMap?.isin !== undefined) {
            return overrideMap.isin;
        }
        if (data?.identifiers !== undefined) {
            const isins = data.identifiers.filter((i) => i.type === 'ISIN' && 'value' in i);
            if (isins.length === 1) {
                return isins[0].value;
            }
            if (isins.length > 1) {
                const isin144A = isins.find(i => i.regulation === 'RULE_144A');
                if (isin144A !== undefined) {
                    return isin144A.value;
                }
                return isins[0].value;
            }
        }
        return undefined;
    },
    isins: ({ data, override, overrideMap }) => {
        if (override?.identifiers !== undefined) {
            return override.identifiers.filter((i) => i.type === 'ISIN' && i.value !== undefined && i.value !== '')
                .map((i) => (i.value ?? "ERROR") + (i.regulation !== undefined ? ` (${i.regulation === 'REGS' ? 'RegS' : '144A'})` : '')).join(', ');
        }
        if (overrideMap?.isin !== undefined) {
            return overrideMap.isin;
        }
        if (data?.identifiers !== undefined) {
            return data.identifiers.filter((i) => i.type === 'ISIN' && i.value !== undefined && i.value !== '')
                .map((i) => (i.value ?? "ERROR") + (i.regulation !== undefined ? ` (${i.regulation === 'REGS' ? 'RegS' : '144A'})` : '')).join(', ');
        }
        return undefined;
    },
    guarantors: ({ data, meta }) => {
        if (meta?.parties !== undefined) {
            const text = [];
            const issuers = meta.parties.filter(p => p.type === 'GUARANTOR');
            issuers.forEach(issuer => {
                if (issuer.company !== undefined) {
                    text.push(issuer.company.name);
                }
                else {
                    if (issuer.name !== undefined) {
                        text.push(issuer.name);
                    }
                }
            });
            return text.join('  // ');
        }
        if (data?.guarantee?.guarantors !== undefined) {
            const text = [];
            data.guarantee.guarantors.forEach(guarantor => {
                if (guarantor.name !== undefined) {
                    text.push(guarantor.name);
                }
                else if (guarantor.shortName !== undefined) {
                    text.push(guarantor.shortName);
                }
                if (guarantor.branche != null) {
                    text.push(`(${guarantor.branche})`);
                }
                return text.join(' ');
            });
        }
        return undefined;
    },
    issuer: ({ data, meta, overrideMap }) => {
        if (overrideMap?.issuer !== undefined) {
            return overrideMap.issuer;
        }
        if (meta?.parties !== undefined) {
            const text = [];
            const issuers = meta.parties.filter(p => p.type === 'ISSUER');
            issuers.forEach(issuer => {
                if (issuer.company !== undefined) {
                    text.push(issuer.company.name);
                }
                else {
                    if (issuer.name !== undefined) {
                        text.push(issuer.name);
                    }
                }
            });
            return text.join('  // ');
        }
        if (data?.issuer !== undefined) {
            const text = [];
            if (data.issuer.name !== undefined) {
                text.push(data.issuer.name);
            }
            else if (data.issuer.shortName !== undefined) {
                text.push(data.issuer.shortName);
            }
            if (data.issuer.branche != null) {
                text.push(`(${data.issuer.branche})`);
            }
            return text.join(' ');
        }
        return undefined;
    },
    issuerRaw: ({ data, overrideMap }) => {
        if (data?.obligor?.name !== undefined) {
            return `${data.obligor.name.replace(/, acting through.*/, '')} (Obligor)`;
        }
        if (data?.issuer !== undefined) {
            const text = [];
            if (data.issuer.name != null) {
                text.push(data.issuer.name.replace(/, acting through.*/, ''));
            }
            else if (data.issuer.shortName != null) {
                text.push(data.issuer.shortName);
            }
            if (data.issuer.branche != null) {
                text.push(`(${data.issuer.branche})`);
            }
            if (text.length > 0) {
                return text.join(' ');
            }
        }
        return overrideMap?.issuer;
    },
    lei: ({ data, meta, override, overrideMap }) => {
        return override?.issuer?.identifiers?.find(o => o.type === 'LEI')?.value ?? overrideMap?.lei ?? meta?.parties?.find(p => p.type === 'ISSUER')?.company?.lei ?? data?.issuer?.identifiers?.find(o => o.type === 'LEI')?.value;
    },
    maturity: ({ data, override, overrideMap }) => {
        if (override?.maturity?.date !== undefined) {
            return override.maturity.date.print();
        }
        const overrideMaturityPerpetual = override?.maturity?.perpetual;
        if (overrideMaturityPerpetual === true || override?.maturity?.tenor?.perpetual) {
            return 'Perpetual';
        }
        const overrideValue = overrideMap?.maturity;
        if (overrideValue !== undefined) {
            const dateRegex = /(?<year>[0-9]{4})(-(?<month>[0-9]{2})(-(?<day>[0-9]{2}))?)?/;
            const dateMatchArray = dateRegex.exec(overrideValue);
            const groups = dateMatchArray?.groups;
            if (groups !== undefined) {
                const { year } = groups;
                const month = 'month' in groups ? groups.month : undefined;
                const day = 'day' in groups ? groups.day : undefined;
                const yearValue = parseInt(year, 10);
                const monthValue = month !== undefined ? parseInt(month, 10) : 0;
                const dayValue = day !== undefined ? parseInt(day, 10) : 1;
                const realDate = new Date(Date.UTC(yearValue, monthValue - 1, dayValue));
                let precision = 'Y';
                if (month !== undefined)
                    precision = 'YM';
                if (day !== undefined)
                    precision = 'YMD';
                return { value: realDate, options: { precision } };
            }
            return overrideValue;
        }
        ;
        if (data?.maturity !== undefined) {
            const { maturity } = data;
            if (maturity.scheduledDissolutionDate !== undefined) {
                const dateRegex = /(?<year>[0-9]{4})(-(?<month>[0-9]{2})(-(?<day>[0-9]{2}))?)?/;
                const dateMatchArray = dateRegex.exec(maturity.scheduledDissolutionDate);
                const groups = dateMatchArray?.groups;
                if (groups !== undefined) {
                    const { year } = groups;
                    const month = 'month' in groups ? groups.month : undefined;
                    const day = 'day' in groups ? groups.day : undefined;
                    const yearValue = parseInt(year, 10);
                    const monthValue = month !== undefined ? parseInt(month, 10) : 0;
                    const dayValue = day !== undefined ? parseInt(day, 10) : 1;
                    const realDate = new Date(Date.UTC(yearValue, monthValue - 1, dayValue));
                    let precision = 'Y';
                    if (month !== undefined)
                        precision = 'YM';
                    if (day !== undefined)
                        precision = 'YMD';
                    return { value: realDate, options: { precision } };
                }
                return overrideValue;
            }
            if (maturity.date !== undefined) {
                return maturity.date.print();
            }
            if (maturity.perpetual || maturity.tenor?.perpetual) {
                return 'Perpetual';
            }
        }
        return undefined;
    },
    minDenomination: ({ data, override, overrideMap }) => {
        if (overrideMap?.denominations !== undefined) {
            return overrideMap.denominations;
        }
        const globalCurrency = data?.currency();
        if (override?.documentation?.denominations !== undefined) {
            return override.documentation.denominations.print(globalCurrency);
        }
        return data?.documentation?.denominations?.print(globalCurrency);
    },
    newIssueConcession: () => {
        return "N/A";
    },
    oversubscription: ({ data, override, overrideMap }) => {
        if (overrideMap?.oversubscription !== undefined) {
            return textToNumber(overrideMap.oversubscription);
        }
        let bookSize;
        if (overrideMap?.books !== undefined) {
            const overrideBookSize = Number.parseInt(overrideMap.books, 10) * 1000000;
            if (!Number.isNaN(overrideBookSize)) {
                bookSize = overrideBookSize;
            }
            else {
                return undefined;
            }
        }
        if (bookSize === undefined) {
            const books = override?.management?.books ?? data?.management?.books;
            bookSize = books?.size?.value;
        }
        let amount;
        if (overrideMap?.amount !== undefined) {
            const overrideAmount = Number.parseInt(overrideMap.amount, 10) * 1000000;
            if (!Number.isNaN(overrideAmount)) {
                amount = overrideAmount;
            }
        }
        else {
            const nominalAmount = override?.nominal?.amount ?? data?.nominal?.tapAmount ?? data?.nominal?.amount;
            amount = nominalAmount?.size?.value;
        }
        if (amount !== undefined && bookSize !== undefined) {
            return { value: bookSize / amount, options: { maximumFractionDigits: 1 } };
        }
        return undefined;
    },
    pricing: ({ data, override, overrideMap }) => {
        if (overrideMap?.pricing !== undefined) {
            return overrideMap.pricing;
        }
        const basis = override?.basis() ?? data?.basis();
        switch (basis) {
            case 'spread': {
                const spread = override?.pricing?.spread ?? data?.pricing?.spread;
                return spread?.print().join(" ");
            }
            case 'yield': {
                const yieldValue = override?.pricing?.yield ?? data?.pricing?.yield;
                return yieldValue?.print();
            }
            case 'priceCoupon': {
                const items = [];
                if (override?.coupons?.[0].fixedRate !== undefined || override?.coupons?.[0].floatingRate !== undefined) {
                    const [coupon] = override.coupons;
                    const overrideCouponText = coupon.print();
                    if (typeof overrideCouponText === 'string') {
                        items.push(overrideCouponText);
                    }
                    else if (typeof overrideCouponText === 'object') {
                        items.push(overrideCouponText.value.toString());
                    }
                    ;
                }
                else {
                    const coupons = data?.coupons ?? data?.profitRates;
                    if (coupons !== undefined) {
                        const [coupon] = coupons;
                        const dataCouponText = coupon.print();
                        if (typeof dataCouponText === 'string') {
                            items.push(dataCouponText);
                        }
                        else if (typeof dataCouponText === 'object') {
                            items.push(dataCouponText.value.toString());
                        }
                    }
                }
                const priceValue = override?.pricing?.price ?? override?.pricing?.issuePrice ?? data?.pricing?.price ?? data?.pricing?.issuePrice;
                const priceText = priceValue?.toString();
                if (priceText !== undefined)
                    items.push(priceText);
                return items.join(" / ");
            }
            case 'price': {
                const priceValue = override?.pricing?.price ?? override?.pricing?.issuePrice ?? data?.pricing?.price ?? data?.pricing?.issuePrice;
                return priceValue?.toString();
            }
            case 'coupon': {
                if (override?.coupons?.[0].fixedRate !== undefined || override?.coupons?.[0].floatingRate !== undefined) {
                    const [coupon] = override.coupons;
                    return coupon.print();
                }
                const coupons = data?.coupons ?? data?.profitRates;
                if (coupons !== undefined) {
                    const [coupon] = coupons;
                    return coupon.print();
                }
            }
            default:
                const spread = override?.pricing?.spread ?? data?.pricing?.spread;
                if (spread !== undefined)
                    return spread.print().join(" ");
                const yieldValue = override?.pricing?.yield ?? data?.pricing?.yield;
                if (yieldValue !== undefined)
                    return yieldValue.print();
                const priceValue = override?.pricing?.price ?? override?.pricing?.issuePrice ?? data?.pricing?.price ?? data?.pricing?.issuePrice;
                return priceValue?.toString();
        }
    },
    price: ({ data, override, overrideMap }) => {
        if (overrideMap?.price !== undefined) {
            return overrideMap.price;
        }
        const price = override?.pricing?.price ?? override?.pricing?.issuePrice ?? data?.pricing?.price ?? data?.pricing?.issuePrice;
        return price !== undefined ? { value: price } : undefined;
    },
    registration: () => {
        return "TBC";
    },
    pricingDate: ({ data, meta, override, overrideMap }) => {
        const overrideDate = override?.pricingDate ?? overrideMap?.pricingDate;
        if (overrideDate !== undefined) {
            if (overrideDate === 'TBC') {
                return undefined;
            }
            return { value: new Date(overrideDate) };
        }
        if (meta?.pricingDate !== undefined) {
            return { value: meta.pricingDate };
        }
        if (data?.pricingDate !== undefined) {
            return { value: new Date(data.pricingDate) };
        }
        return undefined;
    },
    ratings: ({ data, overrideMap, override }) => {
        if (overrideMap?.ratings && typeof overrideMap.ratings === 'string') {
            return overrideMap.ratings;
        }
        if (((override?.ratings?.values !== undefined) || override?.ratings?.unrated)) {
            return override.ratings.print();
        }
        if (((data?.ratings?.values !== undefined && data.ratings.values.length > 0) || data?.ratings?.unrated)) {
            return data.ratings.print();
        }
        if (data?.issuer?.ratings !== undefined && ((data.issuer.ratings.values !== undefined && data.issuer.ratings.values.length > 0) || data.issuer.ratings.unrated)) {
            return data.issuer.ratings.print();
        }
        return undefined;
    },
    ratingIndividual: (label, agency) => ({ data, overrideMap, override }) => {
        const printRatings = (ratings) => {
            if (ratings.unrated) {
                return '-';
            }
            const { values } = ratings;
            if (values !== undefined) {
                const issueRating = values.find((r) => r.agency === agency)?.value;
                if (issueRating !== undefined) {
                    return issueRating;
                }
                if (!values.some(r => r.agency !== undefined)) {
                    if (values.length > 0 && agency === 'MOODYS') {
                        return values[0].value;
                    }
                    if (values.length > 1 && agency === 'S_P') {
                        return values[1].value;
                    }
                    if (values.length > 2 && agency === 'FITCH') {
                        return values[2].value;
                    }
                }
            }
            return undefined;
        };
        if (override !== undefined) {
            const { ratings } = override;
            if (ratings !== undefined && ((ratings.values !== undefined && ratings.values.length > 0) || ratings.unrated)) {
                return printRatings(ratings);
            }
        }
        if (overrideMap?.[label] && typeof overrideMap[label] === 'string') {
            return overrideMap[label];
        }
        if (data === undefined) {
            return undefined;
        }
        const { ratings } = data;
        if (ratings !== undefined && ((ratings.values !== undefined && ratings.values.length > 0) || ratings.unrated)) {
            return printRatings(ratings);
        }
        const issuerRatings = data.issuer?.ratings;
        if (issuerRatings !== undefined && ((issuerRatings.values !== undefined && issuerRatings.values.length > 0) || issuerRatings.unrated)) {
            return printRatings(issuerRatings);
        }
        return undefined;
    },
    redemption: () => {
        return "TBC";
    },
    regions: (map) => ({ transaction }) => {
        return transaction.regions?.map((t) => map.get(t)).join(', ');
    },
    revisedGuidance: ({ data, override }) => {
        const revisedGuidance = override?.revisedGuidance ?? data?.revisedGuidance;
        return revisedGuidance?.print();
    },
    sector: (map) => ({ transaction }) => {
        return transaction.sector ? map.get(transaction.sector) : undefined;
    },
    settlementDate: ({ data, override, overrideMap }) => {
        if (overrideMap?.settlementDate === 'TBC') {
            return undefined;
        }
        const settlementDateString = override?.settlement?.date ?? overrideMap?.settlementDate ?? data?.settlement?.date;
        return settlementDateString !== undefined ? { value: new Date(settlementDateString) } : undefined;
    },
    spread: ({ data, override, overrideMap }) => {
        if (overrideMap?.spread) {
            return textToNumber(overrideMap.spread, { signDisplay: 'always' });
        }
        const value = override?.pricing?.spread?.value ?? data?.pricing?.spread?.value;
        return value !== undefined ? { value, options: { signDisplay: 'always' } } : undefined;
    },
    status: ({ data, meta, override, overrideMap }) => {
        if (override?.management?.status !== undefined) {
            return override.management.status;
        }
        if (overrideMap?.status !== undefined) {
            return overrideMap.status;
        }
        if (meta?.status !== undefined) {
            return meta.status;
        }
        return data?.management?.status;
    },
    structuringLead: () => {
        return "TBC";
    },
    companySector: (map, field) => ({ meta, overrideMap }) => {
        if (overrideMap && field in overrideMap) {
            const val = overrideMap[field];
            if (typeof val === 'string') {
                return map.get(val) ?? '';
            }
        }
        const issuer = meta?.parties?.find(p => p.type === 'ISSUER');
        const value = issuer?.company?.[field];
        if (value !== undefined) {
            return map.get(value);
        }
        return undefined;
    },
    tap: ({ data, meta, override, overrideMap }) => {
        if (overrideMap?.tap !== undefined) {
            return overrideMap.tap.toUpperCase() === 'TRUE';
        }
        if (override?.tap !== undefined) {
            return override.tap;
        }
        if (meta?.tap !== undefined) {
            return meta.tap;
        }
        if (data?.tap !== undefined) {
            return data.tap;
        }
        return undefined;
    },
    tapText: ({ data, meta, override, overrideMap }) => {
        if (overrideMap?.tap !== undefined) {
            return overrideMap.tap.toUpperCase() === 'TRUE' ? 'Yes' : 'No';
        }
        if (override?.tap !== undefined) {
            return override.tap ? 'Yes' : 'No';
        }
        if (meta?.tap !== undefined) {
            return meta.tap ? 'Yes' : 'No';
        }
        if (data?.tap !== undefined) {
            return data.tap ? 'Yes' : 'No';
        }
        return undefined;
    },
    tenor: ({ data, override, overrideMap }) => {
        if (overrideMap?.tenor !== undefined) {
            return overrideMap.tenor;
        }
        const tenor = override?.maturity?.tenor ?? data?.maturity?.tenor;
        if (tenor !== undefined) {
            return tenor.print();
        }
        const weightedAverageLife = override?.maturity?.weightedAverageLife ?? data?.maturity?.weightedAverageLife;
        if (weightedAverageLife?.periodMultiplier !== undefined) {
            return `${weightedAverageLife.periodMultiplier.toString()}${weightedAverageLife.period} WAL`;
        }
        return undefined;
    },
    ticker: ({ data, override, meta, overrideMap }) => {
        return override?.issuer?.identifiers?.find(i => i.type === 'TICKER')?.value ??
            overrideMap?.ticker ??
            (meta?.parties?.find(p => p.type === 'ISSUER')?.company?.tickers?.length === 1 ? meta.parties.find(p => p.type === 'ISSUER')?.company?.tickers?.[0] : undefined) ??
            data?.issuer?.identifiers?.find(i => i.type === 'TICKER')?.value;
    },
    timing: (type) => ({ data, override }) => {
        if (override?.management?.[type] !== undefined) {
            const value = override.management[type];
            return value.print();
        }
        if (data?.management?.[type] !== undefined) {
            const value = data.management[type];
            return value.print();
        }
        return undefined;
    },
    tranche: (tranche) => {
        const { override, data } = tranche;
        const date = override?.maturity?.date ?? data?.maturity?.date;
        const tenor = override?.maturity?.tenor ?? data?.maturity?.tenor;
        const items = [];
        if (tranche.isTap()) {
            if (date?.year !== undefined && date.monthValue !== undefined) {
                items.push(new Date(date.year, date.monthValue - 1, 1).toLocaleDateString((new Intl.DateTimeFormat()).resolvedOptions().locale, { month: 'short', year: '2-digit' }));
            }
            else if (date?.year !== undefined) {
                items.push(date.year.toString());
            }
        }
        else {
            if (tenor !== undefined) {
                items.push(tenor.print());
            }
        }
        if (tranche.isTap()) {
            items.push("Tap");
        }
        else {
            const coupon = override?.coupons?.[0] ?? data?.coupons?.[0];
            if (coupon?.interestBasis === 'FLOATING' || coupon?.floatingRate !== undefined) {
                items.push("FRN");
            }
        }
        return items.join(" ");
    },
    types: (map) => ({ transaction }) => {
        return transaction.types?.map((t) => map.get(t)).join(', ');
    },
    ultimateParent: ({ meta, overrideMap }) => {
        if (overrideMap?.parent) {
            return overrideMap.parent;
        }
        const issuer = meta?.parties?.find(p => p.type === 'ISSUER');
        if (issuer?.ultimateParent !== undefined) {
            return issuer.ultimateParent.name;
        }
        return undefined;
    },
    verified: () => {
        return "TBC";
    },
    yield: ({ data, override, overrideMap }) => {
        if (overrideMap?.yield !== undefined) {
            return textToNumber(overrideMap.yield);
        }
        const value = override?.pricing?.yield?.value ?? data?.pricing?.yield?.value;
        return value !== undefined ? { value } : undefined;
    },
};
